import { skipToken, useQuery, type UndefinedInitialQueryOptions } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { computed, toRef, toValue, unref } from "vue";
import type { Ref } from "vue";
import { useQueryEnabled } from "./useQueryEnabled";
import { logError } from "@/utils/AppError";
import { djangoViewsetErrorHandler } from "@/utils/DjangoError";
import { useAbility } from "@casl/vue";
import {
  DEFAULT_LIST_NO_RETRY_STATUS_CODES,
  DEFAULT_STALE_TIME,
  mergeNoRetryStatusCodes,
  shouldSuppressQueryError,
  type TBaseFilterQueryOptions,
  useFiltersRef,
} from "./baseQueryOptions";
import { useGenericQueryKey } from "./useGenericQueryKey";
import { JOB_ID_FILTER_KEY, useJobIdQueryParam } from "./useJobIdQueryParam";

/**
 * Configuration options for the `useGenericListQuery` composable.
 *
 * @template TResponseItem - The type of a single item in the response array.
 * @template TFilters - The type of the filters object passed to the fetcher.
 */
export type TGenericListQueryOptions<
  TResponseItem,
  TFilters = unknown,
> = TBaseFilterQueryOptions<TFilters> & {
  /**
   * The asynchronous function that fetches the list data.
   * Receives the unref'd `filters` as an argument.
   */
  fetcher: (filters?: TFilters) => Promise<AxiosResponse<TResponseItem[]>>;
  /**
   * Additional options to pass to Vue Query's `useQuery`.
   * These omit internal options completely managed by the composable (`queryKey`, `queryFn`, `enabled`, `initialData`).
   */
  options?: Omit<
    UndefinedInitialQueryOptions<TResponseItem[], Error, [string, TFilters]>,
    "queryKey" | "queryFn" | "enabled" | "initialData" | "staleTime"
  >;
  /**
   * If true, prevents appending the `filters` to the Vue Query `queryKey`.
   * Defaults to false, meaning queries automatically refetch when filters change.
   */
  justQueryKey?: boolean;
};

/**
 * A generic Vue Query composable to handle fetching lists of data.
 * Wraps TanStack Query's `useQuery` to easily integrate API fetchers with reactive filters,
 * permission checks, and dependent queries.
 *
 * @template TResponseItem - The type of a single item returned in the list.
 * @template TFilters - The type of the filters object.
 *
 * @param {TGenericListQueryOptions<TResponseItem, TFilters>} options - Configuration options for the list query.
 * @returns The Vue Query result object containing `data`, `isLoading`, `error`, etc.
 */
export const useGenericListQuery = <TResponseItem, TFilters = unknown>(
  options: TGenericListQueryOptions<TResponseItem, TFilters>
) => {
  const enabledRef = toRef(options.params?.enabled ?? true);
  // A job-authorized query carries its job as an ordinary filter, so it reaches the request and
  // the query key by the same path as everything else — and `neededKey` holds the query until
  // the job resolves instead of firing a request the API would answer with 403.
  const jobId = useJobIdQueryParam(options.jobSignal);
  const baseFiltersRef = useFiltersRef(options.params?.filters) as Ref<TFilters | undefined>;
  const filtersRef = computed(() =>
    options.jobSignal
      ? ({ ...(baseFiltersRef.value ?? {}), [JOB_ID_FILTER_KEY]: jobId.value } as TFilters)
      : baseFiltersRef.value
  ) as Ref<TFilters | undefined>;
  const queryKeyRef = toRef(options.queryKey) as Ref<readonly any[]>;
  const queryKey = useGenericQueryKey(queryKeyRef, filtersRef, {
    includeSegment: !options.justQueryKey,
  });
  const { can } = useAbility();

  const isEnabled = useQueryEnabled({
    enabled: enabledRef,
    permission: options.permission,
    action: options.action,
    neededKey: computed(() =>
      options.jobSignal
        ? [...((options.neededKey ?? []) as string[]), JOB_ID_FILTER_KEY]
        : options.neededKey
    ) as any,
    filters: filtersRef,
    can: can,
    ignorePendingTermsBlock: options.ignorePendingTermsBlock,
  });

  const noRetryStatusCodes = mergeNoRetryStatusCodes(
    DEFAULT_LIST_NO_RETRY_STATUS_CODES,
    options.noRetryStatusCodes
  );

  const fetchList = async () => {
    try {
      const filters = unref(filtersRef);
      return (await options.fetcher(filters)).data;
    } catch (error) {
      const safeStringify = (val: unknown) => {
        try {
          return JSON.stringify(toValue(val));
        } catch {
          return "[unserializable]";
        }
      };
      logError(error, {
        context: {
          feature: "query",
          step: "fetcher",
          queryKey: safeStringify(queryKeyRef),
          filters: safeStringify(filtersRef),
        },
      });
      if (!shouldSuppressQueryError(options.suppressErrors, error)) {
        djangoViewsetErrorHandler()(error);
      }
      throw error;
    }
  };

  return useQuery<TResponseItem[], Error>({
    queryKey: queryKey as any,
    // `enabled` only governs TanStack's *automatic* fetching; an explicit `refetch()` (e.g.
    // SelectFieldValidator's single-item check) bypasses it. Swapping in `skipToken` while
    // disabled makes the query a genuine no-op — the fetcher is never invoked, no matter how
    // the fetch was triggered — and it resumes automatically when `isEnabled` flips true.
    queryFn: computed(() => (isEnabled.value ? fetchList : skipToken)) as any,
    enabled: isEnabled,
    staleTime: options.staleTime ?? DEFAULT_STALE_TIME,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    refetchOnReconnect: options.refetchOnReconnect,
    ...(options.options ?? {}),
    retry: (failureCount: number, error: any) => {
      const status = error?.response?.status;
      if (status && noRetryStatusCodes.has(status)) {
        return false;
      }
      const retryOption = (options.options as any)?.retry;
      if (typeof retryOption === "function") return retryOption(failureCount, error);
      if (typeof retryOption === "number") return failureCount < retryOption;
      if (typeof retryOption === "boolean") return retryOption;
      return failureCount < 3;
    },
  });
};
